import { AnimalSexMap } from '@/model/Animal.model';
import type { Animal, AnimalApiId, AnimalSexLabel } from '@/model/Animal.model';
import type { AnimalDefinitionApiId, AnimalDefinitionMappingV1 } from '@/model/PlannedAnimal.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import { _isEmptyString, _isNotEmpty, _notNil } from '@/littledash';
import { renderGroupContainer } from '@/components/Studies/Create/Steps/Animals/Animals.utils';
import type Handsontable from 'handsontable';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import type { PlannedAnimalDefinition } from '@/components/Animals/PlannedAnimals/PlannedAnimals.utils';
import type { Nullable } from '@/model/Common.model';
import type { SpreadsheetSettings } from '@/components/UI/SpreadSheet/Spreadsheet';
import type { RefObject } from 'react';

export interface MapAnimalRow {
  cageName: string;
  animalName: string;
  sex: AnimalSexLabel;
  group: string;
  animalApiId: AnimalApiId;
  plannedId: string | null;
  plannedApiId: AnimalDefinitionApiId;
}

export const transformAnimalsToMapData = (
  animals: Array<Animal>,
  plannedAnimals: Array<PlannedAnimalDefinition>
): Array<MapAnimalRow> => {
  const usedPlannedIds = new Set<string>();
  const plannedAnimalsIdMap = new Map(
    plannedAnimals.map((animal) => [animal.planned_id, animal.api_id as AnimalDefinitionApiId])
  );

  return animals.map((animal) => {
    const availablePlannedId =
      (plannedAnimals ?? [])
        ?.filter(
          (plannedAnimal) =>
            plannedAnimal.sex === animal.sex && plannedAnimal.study_group_api_id === animal.study_group?.api_id
        )
        .find((plannedAnimal) => !usedPlannedIds.has(plannedAnimal.planned_id))?.planned_id ?? null;
    if (_notNil(availablePlannedId)) {
      usedPlannedIds.add(availablePlannedId);
    }
    return {
      cageName: animal.cage?.name ?? '',
      animalName: animal.name ?? '',
      sex: AnimalSexMap[animal.sex],
      group: animal.study_group?.name ?? '',
      animalApiId: animal.api_id as AnimalApiId,
      plannedId: availablePlannedId,
      plannedApiId: plannedAnimalsIdMap.get(availablePlannedId ?? '') as AnimalDefinitionApiId,
    };
  });
};

export const createAnimalMappings = (mapData: Array<MapAnimalRow>) => {
  return mapData.reduce<Array<AnimalDefinitionMappingV1>>((acc, row) => {
    if (_notNil(row.plannedId)) {
      acc.push({
        animal_api_id: row.animalApiId,
        animal_definition_api_id: row.plannedApiId,
      });
    }
    return acc;
  }, []);
};

interface MapAnimalsColumnDataResult {
  columns: Array<Record<string, unknown>>;
  colHeaders: Array<string>;
  hiddenColumns: { columns: Array<number>; indicators: boolean };
}

export const mapAnimalsColumnData = (
  groups: Array<TreatmentGroup>,
  hotInstanceRef: RefObject<HotTableRef | null>
): MapAnimalsColumnDataResult => {
  return {
    columns: [
      {
        data: 'cageName',
        title: 'Cage Name',
        readOnly: true,
        width: 150,
      },
      {
        data: 'animalName',
        title: 'Animal Name',
        readOnly: true,
        width: 150,
      },
      {
        data: 'sex',
        title: 'Sex',
        readOnly: true,
        width: 100,
      },
      {
        data: 'group',
        title: 'Group',
        readOnly: true,
        width: 150,
        renderer: function (
          _instance: Handsontable,
          td: HTMLTableCellElement,
          _row: number,
          _col: number,
          _prop: string | number,
          value: string | null
        ): void {
          const container = renderGroupContainer(groups, value);
          td.className = 'htMiddle htDimmed';
          td.innerHTML = '';
          td.appendChild(container);
        },
      },
      {
        data: 'plannedId',
        title: 'Planned animal ID',
        type: 'dropdown',
        source: [],
        width: 150,
        allowEmpty: true,
        validator: function (this: { row: number; col: number }, value: string, callback: (valid: boolean) => void) {
          validateMapAnimalId(value ?? '', callback, hotInstanceRef?.current?.hotInstance ?? null, this.row, this.col);
        },
        validationErrorMessage: () => 'ID must be selected from dropdown and must be unique.',
      },
      {
        data: 'animalApiId',
        type: 'text',
        readOnly: true,
      },
    ],
    colHeaders: ['Cage Name', 'Animal Name', 'Sex', 'Group', 'Planned Animal ID'],
    hiddenColumns: { columns: [5], indicators: false },
  };
};

export const mapAnimalsSettings = (columnData: MapAnimalsColumnDataResult): SpreadsheetSettings => {
  return {
    ...columnData,
    contextMenu: false,
    manualColumnResize: true,
    rowHeaders: false,
    outsideClickDeselects: true,
    minSpareRows: 0,
    columnSorting: false,
    className: 'htMiddle',
    rowHeights: 50,
    height: 'calc( 90vh - 275px )',
  };
};

export const updateCellMeta = (
  hotInstance: Handsontable,
  mapData: Array<MapAnimalRow>,
  groups: Array<TreatmentGroup>,
  plannedAnimals: Array<PlannedAnimalDefinition>
): void => {
  const idCol = hotInstance.propToCol('plannedId') as number;

  mapData.forEach((rowData, rowIndex) => {
    const selectedGroup = groups.find((group) => group.name === rowData.group);
    const groupApiId = selectedGroup?.api_id;

    if (_notNil(groupApiId)) {
      const matchingIds = plannedAnimals
        ?.filter(
          (plannedAnimal) =>
            plannedAnimal.study_group_api_id === groupApiId && AnimalSexMap[plannedAnimal.sex] === rowData.sex
        )
        .map((animal) => animal.planned_id);

      if (_isNotEmpty(matchingIds)) {
        hotInstance.setCellMeta(rowIndex, idCol, 'source', matchingIds);
        hotInstance.setCellMeta(rowIndex, idCol, 'allowEmpty', true);
      } else {
        hotInstance.setCellMeta(rowIndex, idCol, 'readOnly', true);
      }
    }
  });
};

export const createPlannedIdCellsConfig = (
  mapData: Array<MapAnimalRow>,
  groups: Array<TreatmentGroup>,
  plannedAnimals: Array<PlannedAnimalDefinition>
): ((row: number, col: number, prop: string | number) => Record<string, unknown>) => {
  const rowConfigs = mapData.map((rowData) => {
    const selectedGroup = groups.find((group) => group.name === rowData.group);
    const groupApiId = selectedGroup?.api_id;

    if (_notNil(groupApiId)) {
      const matchingIds = plannedAnimals
        .filter(
          (plannedAnimal) =>
            plannedAnimal.study_group_api_id === groupApiId && AnimalSexMap[plannedAnimal.sex] === rowData.sex
        )
        .map((animal) => animal.planned_id);

      if (_isNotEmpty(matchingIds)) {
        return { source: matchingIds, allowEmpty: true };
      }
      return { readOnly: true };
    }
    return undefined;
  });

  return (row: number, _col: number, prop: string | number) => {
    if (prop === 'plannedId' && row < rowConfigs.length) {
      const config = rowConfigs[row];
      if (_notNil(config)) {
        return config;
      }
    }
    return {};
  };
};

export const processDataChange = (
  hotInstance: Handsontable,
  plannedAnimals: Array<PlannedAnimalDefinition>
): Array<MapAnimalRow> => {
  const currentData = hotInstance.getSourceData() as Array<MapAnimalRow>;
  return currentData.map((row) => ({
    ...row,
    plannedApiId: plannedAnimals.find((animal) => animal.planned_id === row.plannedId)?.api_id ?? row.plannedApiId,
  }));
};

const validateMapAnimalId = (
  value: string,
  callback: (valid: boolean) => void,
  hot: Nullable<Handsontable>,
  row: number,
  col: number
) => {
  if (_isEmptyString(value)) {
    return callback(true);
  }
  if (_notNil(hot)) {
    const availableIds = (hot.getCellMeta(row, col)?.source ?? []) as Array<string>;

    if (!availableIds.includes(value)) {
      return callback(false);
    }
    const currentData = hot.getSourceData() as Array<MapAnimalRow>;
    const duplicateIDs = currentData.filter((dataRow) => _notNil(value) && dataRow.plannedId === value).length;
    return callback(duplicateIDs <= 1);
  }
  return callback(false);
};
